// Small hand-drawn charts of the Activity view (SVG/CSS, no chart library):
// they follow the view's own colours and stay crisp at any size.
import { useEffect, useMemo, useRef, useState } from 'react';
import { CATEGORIES, bucketLabel, num } from './format';

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(Math.floor(entry.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** A tiny trend line with a soft area under it. */
export function Spark({ values, color = 'var(--ac-accent)', height = 34 }) {
  const [ref, width] = useWidth();
  const id = useMemo(() => `sp${Math.random().toString(36).slice(2, 8)}`, []);
  const vs = values && values.length ? values : [0];
  const max = Math.max(...vs, 1);
  const w = Math.max(width, 10);
  const step = vs.length > 1 ? w / (vs.length - 1) : w;
  const pts = vs.map((v, i) => [i * step, height - 3 - (v / max) * (height - 8)]);
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return (
    <div ref={ref} className="ac-spark" style={{ height }}>
      {width > 0 && (
        <svg width={w} height={height} viewBox={`0 0 ${w} ${height}`} aria-hidden="true">
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.35" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          <path d={`${line} L${w},${height} L0,${height} Z`} fill={`url(#${id})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
        </svg>
      )}
    </div>
  );
}

/** Stacked bars per day (or hour) and category, with a hover card. */
export function StackedBars({ rows, hourly, height = 230, hidden = [] }) {
  const [ref, width] = useWidth();
  const [hover, setHover] = useState(null);
  const cats = CATEGORIES.filter((c) => !hidden.includes(c.key));
  const totals = rows.map((r) => cats.reduce((s, c) => s + (r[c.key] || 0), 0));
  const max = Math.max(...totals, 1);
  const top = niceMax(max);
  const padL = 34;
  const padB = 22;
  const plotW = Math.max(width - padL, 10);
  const plotH = height - padB - 6;
  const slot = plotW / Math.max(rows.length, 1);
  const barW = Math.max(Math.min(slot * 0.68, 26), 2);
  const every = Math.max(1, Math.ceil(rows.length / Math.max(Math.floor(plotW / 64), 1)));
  const ticks = [0, top / 2, top];
  return (
    <div ref={ref} className="ac-bars" style={{ height }} onMouseLeave={() => setHover(null)}>
      {width > 0 && (
        <svg width={width} height={height} role="img" aria-label="Activity over time">
          {ticks.map((t) => {
            const y = 6 + plotH - (t / top) * plotH;
            return (
              <g key={t}>
                <line x1={padL} x2={width} y1={y} y2={y} className="ac-gridline" />
                <text x={padL - 8} y={y + 4} className="ac-axis" textAnchor="end">{compactTick(t)}</text>
              </g>
            );
          })}
          {rows.map((r, i) => {
            let y = 6 + plotH;
            const x = padL + i * slot + (slot - barW) / 2;
            return (
              <g key={r.key} onMouseEnter={() => setHover(i)}>
                <rect x={padL + i * slot} y={6} width={slot} height={plotH} fill="transparent" />
                {cats.map((c) => {
                  const v = r[c.key] || 0;
                  if (!v) return null;
                  const h = Math.max((v / top) * plotH, 1.5);
                  y -= h;
                  return <rect key={c.key} x={x} y={y} width={barW} height={h} rx={Math.min(3, barW / 3)}
                    className={`ac-fill-${c.key}`} opacity={hover == null || hover === i ? 1 : 0.38} />;
                })}
                {i % every === 0 && (
                  <text x={x + barW / 2} y={height - 6} className="ac-axis" textAnchor="middle">{bucketLabel(r.key, hourly)}</text>
                )}
              </g>
            );
          })}
        </svg>
      )}
      {hover != null && rows[hover] && (
        <div className="ac-tip" style={{ left: Math.min(Math.max(padL + hover * slot + slot / 2, 90), width - 90) }}>
          <div className="ac-tip-head">{bucketLabel(rows[hover].key, hourly)}{!hourly && ` ${rows[hover].key.slice(0, 4)}`}</div>
          {cats.filter((c) => rows[hover][c.key]).map((c) => (
            <div key={c.key} className="ac-tip-row"><i className={`ac-dot ac-bg-${c.key}`} />{c.label}<b>{num(rows[hover][c.key])}</b></div>
          ))}
          {!totals[hover] && <div className="ac-tip-row muted">Nothing happened</div>}
        </div>
      )}
    </div>
  );
}

function niceMax(v) {
  const p = 10 ** Math.floor(Math.log10(v));
  const n = v / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}
const compactTick = (t) => (t >= 1000 ? `${Math.round(t / 100) / 10}k` : String(Math.round(t)));

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Weekday × hour grid: when people work. */
export function Heatmap({ cells }) {
  const grid = Array.from({ length: 7 }, () => Array(24).fill(0));
  (cells || []).forEach((c) => { if (c.dow >= 1 && c.dow <= 7) grid[c.dow - 1][c.hour] += c.n; });
  const max = Math.max(1, ...grid.flat());
  const [hover, setHover] = useState(null);
  return (
    <div className="ac-heat" onMouseLeave={() => setHover(null)}>
      <div className="ac-heat-grid">
        {grid.map((row, d) => (
          <div key={DAYS[d]} className="ac-heat-row">
            <span className="ac-heat-day">{DAYS[d]}</span>
            {row.map((n, h) => (
              <i key={h} onMouseEnter={() => setHover({ d, h, n })}
                style={{ '--ac-heat': n ? 0.16 + 0.84 * Math.sqrt(n / max) : 0 }}
                className={n ? 'on' : ''} />
            ))}
          </div>
        ))}
        <div className="ac-heat-row ac-heat-hours">
          <span />
          {Array.from({ length: 24 }, (_, h) => <span key={h}>{h % 6 === 0 ? `${h}` : ''}</span>)}
        </div>
      </div>
      <div className="ac-heat-foot">
        {hover ? <><b>{DAYS[hover.d]} {String(hover.h).padStart(2, '0')}:00–{String(hover.h + 1).padStart(2, '0')}:00</b> · {num(hover.n)} actions</>
          : <>Darker = busier · local time</>}
      </div>
    </div>
  );
}

/** A labelled bar list: [{ label, value, sub, tone }]. */
export function BarList({ items, empty = 'Nothing yet' }) {
  const max = Math.max(1, ...items.map((i) => i.value));
  if (!items.length) return <div className="ac-empty small">{empty}</div>;
  return (
    <div className="ac-barlist">
      {items.map((i) => (
        <div key={i.label} className="ac-barlist-row">
          <div className="ac-barlist-text"><span>{i.label}</span>{i.sub && <em>{i.sub}</em>}</div>
          <div className="ac-barlist-track"><i className={i.tone ? `ac-bg-${i.tone}` : ''} style={{ width: `${(i.value / max) * 100}%` }} /></div>
          <b>{num(i.value)}</b>
        </div>
      ))}
    </div>
  );
}

/** A person's days as small bars (last `days` days). */
export function DayStrip({ days, count = 14 }) {
  const out = [];
  const now = new Date();
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    out.push({ k, n: days?.[k] || 0, weekend: d.getDay() === 0 || d.getDay() === 6 });
  }
  const max = Math.max(1, ...out.map((o) => o.n));
  return (
    <div className="ac-strip" title={`${out.reduce((s, o) => s + o.n, 0)} actions in ${count} days`}>
      {out.map((o) => <i key={o.k} className={`${o.n ? 'on' : ''}${o.weekend ? ' we' : ''}`} style={{ height: `${o.n ? 22 + 78 * (o.n / max) : 10}%` }} />)}
    </div>
  );
}
